import React from 'react';
import { motion } from 'framer-motion';
import { 
  Dumbbell, 
  Heart, 
  Zap, 
  Timer, 
  TrendingUp, 
  Activity,
  Target,
  Award,
  Calendar,
  ArrowLeft
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar
} from 'recharts';
import StatCard from './StatCard';

const PhysicalActivityStats = ({ isDarkMode = true, onBackToOverview }) => {
  const physicalData = {
    fitnessScore: 88,
    weeklyWorkouts: 5,
    totalActiveMinutes: 420,
    caloriesBurned: 2150,
    heartRateAvg: 72,
    weeklyProgress: [
      { day: 'Pzt', workouts: 1, calories: 350, heartRate: 75, activeMinutes: 60 },
      { day: 'Sal', workouts: 0, calories: 0, heartRate: 70, activeMinutes: 0 },
      { day: 'Çar', workouts: 1, calories: 420, heartRate: 78, activeMinutes: 80 },
      { day: 'Per', workouts: 1, calories: 380, heartRate: 76, activeMinutes: 70 },
      { day: 'Cum', workouts: 1, calories: 450, heartRate: 80, activeMinutes: 90 },
      { day: 'Cmt', workouts: 1, calories: 320, heartRate: 72, activeMinutes: 60 },
      { day: 'Paz', workouts: 0, calories: 230, heartRate: 68, activeMinutes: 60 }
    ],
    workoutTypes: [
      { name: 'Kardiyovasküler', value: 45, color: '#FF6B6B' },
      { name: 'Güç Antrenmanı', value: 30, color: '#4ECDC4' },
      { name: 'Esneklik', value: 15, color: '#45B7D1' },
      { name: 'Spor', value: 10, color: '#96CEB4' }
    ],
    personalRecords: {
      longestRun: '12.5 km',
      maxHeartRate: 180,
      weightLifted: '85 kg',
      fastestMile: '6:45',
      totalWorkouts: 156
    },
    todaysActivities: [
      { name: 'Sabah Koşusu', duration: 30, completed: true, xp: 25, calories: 320 },
      { name: 'Güç Antrenmanı', duration: 45, completed: true, xp: 35, calories: 280 },
      { name: 'Yoga', duration: 20, completed: false, xp: 15, calories: 80 },
      { name: 'Yürüyüş', duration: 30, completed: false, xp: 20, calories: 150 }
    ],
    monthlyGoals: [
      { name: '20 Antrenman', current: 16, target: 20, progress: 80 },
      { name: '5000 Kalori', current: 4200, target: 5000, progress: 84 },
      { name: '300 Aktif Dakika', current: 280, target: 300, progress: 93 }
    ],
    achievements: [
      { name: 'Kardiyovasküler Kahraman', description: '50 kardiyovasküler antrenman', progress: 92 },
      { name: 'Güç Ustası', description: '100 güç antrenmanı', progress: 67 },
      { name: 'Tutarlılık Şampiyonu', description: '30 gün düzenli antrenman', progress: 45 }
    ],
    weeklyXP: 480,
    streak: 8
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'];
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Back Button */}
      {onBackToOverview && (
        <motion.div variants={itemVariants}>
          <button
            onClick={onBackToOverview}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800'
            }`}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>İstatistiklere Geri Dön</span>
          </button>
        </motion.div>
      )}

      {/* Fitness Score Overview */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-red-900 to-orange-900' : 'bg-gradient-to-br from-red-50 to-orange-50'} shadow-lg border-2 ${isDarkMode ? 'border-red-500/20' : 'border-red-200'}`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                💪 Fiziksel Aktivite Skoru
              </h2>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Fitness ve aktivite performansınız
              </p>
            </div>
            <div className="text-center">
              <div className={`text-4xl font-bold text-red-500 mb-2`}>
                {physicalData.fitnessScore}
              </div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                /100
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics Cards */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Haftalık Antrenman" 
            icon={<Dumbbell />} 
            value={physicalData.weeklyWorkouts} 
            unit="oturum" 
            progress={physicalData.weeklyWorkouts * 20}
            trend={25}
            color="#FF6B6B"
            isDarkMode={isDarkMode}
            index={0}
          />
          <StatCard 
            title="Aktif Dakika" 
            icon={<Timer />} 
            value={physicalData.totalActiveMinutes} 
            unit="dk" 
            progress={70}
            trend={15}
            color="#4ECDC4"
            isDarkMode={isDarkMode}
            index={1}
          />
          <StatCard 
            title="Kalori Yakımı" 
            icon={<Zap />} 
            value={physicalData.caloriesBurned} 
            unit="kcal" 
            progress={85}
            trend={12}
            color="#45B7D1"
            isDarkMode={isDarkMode}
            index={2}
          />
          <StatCard 
            title="Ortalama Nabız" 
            icon={<Heart />} 
            value={physicalData.heartRateAvg} 
            unit="bpm" 
            progress={60}
            trend={-3}
            color="#96CEB4"
            isDarkMode={isDarkMode}
            index={3}
          />
        </div>
      </motion.div>

      {/* Weekly Progress and Workout Types */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Progress Chart */}
        <motion.div variants={itemVariants}>
          <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-blue-500" />
              Haftalık Aktivite İlerlemesi
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={physicalData.weeklyProgress}>
                  <XAxis dataKey="day" stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                  <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                      borderColor: isDarkMode ? '#374151' : '#E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="calories" fill="#FF6B6B" name="Kalori" />
                  <Bar dataKey="activeMinutes" fill="#4ECDC4" name="Aktif Dakika" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Workout Types Distribution */}
        <motion.div variants={itemVariants}>
          <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-green-500" />
              Antrenman Türü Dağılımı
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={physicalData.workoutTypes}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {physicalData.workoutTypes.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Today's Activities */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-blue-500" />
            Bugünün Aktiviteleri
          </h3>
          <div className="space-y-3">
            {physicalData.todaysActivities.map((activity, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-4 rounded-lg ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                } ${activity.completed ? 'border-l-4 border-green-500' : ''}`}
              >
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    activity.completed ? 'bg-green-500' : 'bg-gray-400'
                  }`} />
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {activity.name}
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {activity.duration} dakika • {activity.calories} kalori
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-bold ${
                    activity.completed ? 'text-green-500' : 'text-gray-400'
                  }`}>
                    +{activity.xp} XP
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Personal Records */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Award className="h-5 w-5 mr-2 text-amber-500" />
            Kişisel Rekorlar
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Object.entries(physicalData.personalRecords).map(([key, value], index) => (
              <div key={index} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                <div className="text-2xl font-bold text-amber-500 mb-2">
                  {value}
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {key === 'longestRun' && 'En Uzun Koşu'}
                  {key === 'maxHeartRate' && 'Maks Nabız'}
                  {key === 'weightLifted' && 'Kaldırılan Ağırlık'}
                  {key === 'fastestMile' && 'En Hızlı Mil'}
                  {key === 'totalWorkouts' && 'Toplam Antrenman'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Monthly Goals Progress */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2 text-purple-500" />
            Aylık Hedefler
          </h3>
          <div className="space-y-4">
            {physicalData.monthlyGoals.map((goal, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {goal.name}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {goal.current} / {goal.target}
                    </p>
                  </div>
                  <span className={`text-sm font-bold ${
                    goal.progress >= 90 ? 'text-green-500' : goal.progress >= 70 ? 'text-blue-500' : 'text-orange-500'
                  }`}>
                    {goal.progress}%
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <motion.div
                    className={`h-full rounded-full ${
                      goal.progress >= 90 ? 'bg-green-500' : goal.progress >= 70 ? 'bg-blue-500' : 'bg-orange-500'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${goal.progress}%` }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Progress Summary */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
            <div className="text-2xl font-bold text-red-500 mb-2">
              {physicalData.weeklyXP}
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Haftalık XP
            </p>
          </div>
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
            <div className="text-2xl font-bold text-orange-500 mb-2">
              {physicalData.streak}
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Gün Seri
            </p>
          </div>
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
            <div className="text-2xl font-bold text-green-500 mb-2">
              {physicalData.achievements.filter(a => a.progress === 100).length}
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Tamamlanan Başarım
            </p>
          </div>
        </div>
      </motion.div>

      {/* Achievements Progress */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Award className="h-5 w-5 mr-2 text-amber-500" />
            Fiziksel Aktivite Başarımları
          </h3>
          <div className="space-y-4">
            {physicalData.achievements.map((achievement, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {achievement.name}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {achievement.description}
                    </p>
                  </div>
                  <span className={`text-sm font-bold ${
                    achievement.progress === 100 ? 'text-green-500' : 'text-blue-500'
                  }`}>
                    {achievement.progress}%
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <motion.div
                    className={`h-full rounded-full ${
                      achievement.progress === 100 ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${achievement.progress}%` }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PhysicalActivityStats;
